import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiSettingsApi, type AiProvider, type AiSettings, type SaveAiKeyInput } from '@/lib/api/ai-settings';

export const aiSettingsKeys = {
  all: ['ai-settings'] as const,
  balance: () => [...aiSettingsKeys.all, 'balance'] as const,
  usage: () => [...aiSettingsKeys.all, 'usage'] as const,
};

/** This month's AI usage on the caller's key (and the company key, for admins). */
export function useAiUsage() {
  return useQuery({
    queryKey: aiSettingsKeys.usage(),
    queryFn: () => aiSettingsApi.usage(),
    select: (response) => response.data,
    refetchInterval: 60_000,
  });
}

/** Starts a request interview on the API key; resolves to the new job. */
export function useStartKeyInterview() {
  return useMutation({
    mutationFn: (input: { epicRequestId?: string; project?: string }) => aiSettingsApi.startKeyInterview(input),
  });
}

export function useAiSettings() {
  return useQuery({
    queryKey: aiSettingsKeys.all,
    queryFn: () => aiSettingsApi.get(),
    select: (response) => response.data,
  });
}

/** Remaining credits on the key runs would use. Only asked when the provider reports it. */
export function useAiBalance(enabled: boolean) {
  return useQuery({
    queryKey: aiSettingsKeys.balance(),
    queryFn: () => aiSettingsApi.balance(),
    select: (response) => response.data,
    enabled,
    refetchInterval: 60_000,
  });
}

function useSettingsMutation<TInput>(fn: (input: TInput) => Promise<{ data: AiSettings }>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (response) => {
      queryClient.setQueryData(aiSettingsKeys.all, response);
      void queryClient.invalidateQueries({ queryKey: aiSettingsKeys.balance() });
    },
  });
}

export type KeyOwner = 'personal' | 'company';

export function useSaveAiKey(owner: KeyOwner) {
  return useSettingsMutation((input: SaveAiKeyInput) =>
    owner === 'personal' ? aiSettingsApi.savePersonalKey(input) : aiSettingsApi.saveCompanyKey(input)
  );
}

export function useDeleteAiKey(owner: KeyOwner) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => (owner === 'personal' ? aiSettingsApi.deletePersonalKey() : aiSettingsApi.deleteCompanyKey()),
    onSuccess: (response) => {
      queryClient.setQueryData(aiSettingsKeys.all, response);
      void queryClient.invalidateQueries({ queryKey: aiSettingsKeys.balance() });
    },
  });
}

export function useVerifyAiKey() {
  return useMutation({
    mutationFn: ({ provider, apiKey }: { provider: AiProvider; apiKey: string }) =>
      aiSettingsApi.verifyKey(provider, apiKey),
  });
}
